if (!customElements.get('product-free-shipping-status')) {
  /**
   * @param {number} cents
   * @param {string} currency
   */
  function formatCartMoney(cents, currency) {
    const code = currency || 'ARS';
    const n = Math.max(0, cents) / 100;
    try {
      return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: code,
        maximumFractionDigits: 0,
        currencyDisplay: 'narrowSymbol',
      }).format(n);
    } catch {
      return `$${Math.round(n).toLocaleString('es-AR')}`;
    }
  }

  class ProductFreeShippingStatus extends HTMLElement {
    connectedCallback() {
      this.thAmba = Number(this.dataset.thresholdAmba) || 0;
      this.thInterior = Number(this.dataset.thresholdInterior) || 0;
      this.variantPrice = Number(this.dataset.variantPrice) || 0;
      this.forceSplit = this.dataset.forceSplit === 'true';
      this.currency = this.dataset.currency || 'ARS';

      const initial = Number(this.dataset.initialTotal);
      if (!Number.isNaN(initial)) {
        this.applyTotals(initial);
      }

      this.boundRefresh = this.refreshFromCart.bind(this);
      document.addEventListener('on:cart:add', this.boundRefresh);
      document.addEventListener('on:line-item:change', this.boundRefresh);
    }

    disconnectedCallback() {
      document.removeEventListener('on:cart:add', this.boundRefresh);
      document.removeEventListener('on:line-item:change', this.boundRefresh);
    }

    async refreshFromCart() {
      if (!window.theme?.routes?.cart) return;
      try {
        const res = await fetch(`${window.theme.routes.cart}.js`);
        if (!res.ok) return;
        const cart = await res.json();
        if (cart && typeof cart.total_price === 'number') {
          if (this.forceSplit && (!cart.item_count || cart.item_count === 0)) {
            this.hidden = true;
            this.replaceChildren();
            return;
          }
          this.hidden = false;
          this.applyTotals(cart.total_price);
        }
      } catch {
        /* ignore */
      }
    }

    createTruckIcon() {
      const wrap = document.createElement('span');
      wrap.className = 'product-free-shipping-regions__icon';
      const img = document.createElement('img');
      img.className = 'product-free-shipping-regions__icon-img';
      img.src = this.dataset.truckSrc || '';
      img.alt = '';
      img.width = 28;
      img.height = 28;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.setAttribute('aria-hidden', 'true');
      wrap.appendChild(img);
      return wrap;
    }

    /**
     * @param {'summary'|'amba'|'interior'} region
     * @param {boolean} qualified
     */
    buildLine(region, qualified, bodyNodes) {
      const p = document.createElement('p');
      p.className = 'product-free-shipping-regions__line';
      p.dataset.region = region;
      if (qualified) p.classList.add('product-free-shipping-regions__line--qualified');
      p.appendChild(this.createTruckIcon());
      const body = document.createElement('span');
      body.className = 'product-free-shipping-regions__body';
      bodyNodes.forEach((node) => body.appendChild(node));
      p.appendChild(body);
      return p;
    }

    /**
     * @param {number} rem
     * @param {number} threshold
     * @param {string} regionPhrase
     */
    fillRegionalBody(rem, threshold, regionPhrase) {
      const cartOk = rem === 0;
      const productOk = !this.forceSplit && !cartOk && this.variantPrice >= threshold;

      if (cartOk) {
        return {
          qualified: true,
          nodes: [document.createTextNode(`Tu carrito alcanza envío gratis en ${regionPhrase}`)],
        };
      }
      if (productOk) {
        return {
          qualified: true,
          nodes: [document.createTextNode(`Si agregás este producto tenés envío gratis en ${regionPhrase}`)],
        };
      }
      const amount = document.createElement('span');
      amount.className = 'product-free-shipping-regions__amount';
      amount.textContent = formatCartMoney(rem, this.currency);
      return {
        qualified: false,
        nodes: [
          document.createTextNode('Te faltan '),
          amount,
          document.createTextNode(` para tener envío gratis en ${regionPhrase}`),
        ],
      };
    }

    /**
     * @param {number} cartTotalCents
     */
    applyTotals(cartTotalCents) {
      const remAmba = this.thAmba > 0 ? Math.max(0, this.thAmba - cartTotalCents) : 0;
      const remInterior = this.thInterior > 0 ? Math.max(0, this.thInterior - cartTotalCents) : 0;

      const needAmba = this.thAmba > 0 && remAmba > 0;
      const needInterior = this.thInterior > 0 && remInterior > 0;
      const cartFullyOk = !needAmba && !needInterior;

      let addProductOk = false;
      if (!this.forceSplit && !cartFullyOk) {
        if (needAmba && this.variantPrice >= this.thAmba) addProductOk = true;
        if (needInterior && this.variantPrice >= this.thInterior) addProductOk = true;
      }

      const useCompact = cartFullyOk || (!this.forceSplit && addProductOk);

      this.replaceChildren();

      if (useCompact) {
        this.classList.remove('product-free-shipping-regions--split');
        let qualified = true;
        const nodes = [];
        if (cartFullyOk) {
          nodes.push(document.createTextNode('Tu carrito alcanza envío gratis'));
        } else {
          nodes.push(document.createTextNode('Si agregás este producto tenés envío gratis'));
        }
        this.appendChild(this.buildLine('summary', qualified, nodes));
      } else {
        this.classList.add('product-free-shipping-regions--split');
        if (this.thAmba > 0) {
          const amba = this.fillRegionalBody(remAmba, this.thAmba, 'AMBA');
          this.appendChild(this.buildLine('amba', amba.qualified, amba.nodes));
        }
        if (this.thInterior > 0) {
          const intl = this.fillRegionalBody(remInterior, this.thInterior, 'el Interior del país');
          this.appendChild(this.buildLine('interior', intl.qualified, intl.nodes));
        }
      }
    }
  }

  customElements.define('product-free-shipping-status', ProductFreeShippingStatus);
}
